import { useState } from 'react';
import { AlertTriangle, Lock, Paperclip, UserCheck } from 'lucide-react';
import { COLUMNS, CATEGORY_META, PRIORITY_CLASS, fmtDate, isOverdue } from '../../lib/planConstants';

export const TaskCard = ({ task, onOpen, onDragStart, blocked }) => {
  const cat = CATEGORY_META[task.category] || CATEGORY_META.personal;
  const overdue = isOverdue(task);
  return (
    <div
      draggable
      onDragStart={() => onDragStart(task)}
      onClick={() => onOpen(task)}
      data-testid={`task-card-${task.id}`}
      className="rounded-xl border border-slate-700/80 bg-slate-900/70 p-3 cursor-pointer hover:border-cyan-500/50 hover:bg-slate-900 transition-colors duration-200"
    >
      <div className="flex items-center gap-2 mb-1.5">
        <span className={`text-[11px] px-2 py-0.5 rounded-full ${cat.bg} ${cat.text}`}>{cat.label}</span>
        <span className={`text-[11px] px-2 py-0.5 rounded-full ml-auto ${PRIORITY_CLASS[task.priority] || ''}`}>{task.priority}</span>
      </div>
      <p className="text-sm text-white font-medium leading-snug">{task.title}</p>
      {task.milestone_title && <p className="text-[11px] text-slate-300 mt-1">{task.milestone_title}</p>}
      <div className="flex flex-wrap items-center gap-2 mt-2 text-[11px]">
        <span className={overdue ? 'text-rose-300 font-semibold' : 'text-slate-300'}>
          {overdue && <AlertTriangle className="w-3 h-3 inline mr-1" />}Due {fmtDate(task.due_date)}
        </span>
        {blocked && <span className="text-amber-300 inline-flex items-center gap-1"><Lock className="w-3 h-3" />blocked</span>}
        {(task.evidence || []).length > 0 && <span className="text-cyan-300 inline-flex items-center gap-1"><Paperclip className="w-3 h-3" />{task.evidence.length}</span>}
        {task.needs_review !== 'none' && task.needs_review && (
          <span className="text-violet-300 inline-flex items-center gap-1"><UserCheck className="w-3 h-3" />{task.needs_review}</span>
        )}
      </div>
    </div>
  );
};

export default function PlanBoard({ tasks, onStatusChange, onOpenTask }) {
  const [dragged, setDragged] = useState(null);
  const completedIds = new Set(tasks.filter((t) => t.status === 'completed').map((t) => t.id));
  const isBlocked = (t) => (t.depends_on || []).some((d) => !completedIds.has(d));

  const drop = (columnId) => {
    if (dragged && dragged.status !== columnId) onStatusChange(dragged, columnId);
    setDragged(null);
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-6 gap-4" data-testid="plan-board">
      {COLUMNS.map((col) => {
        const Icon = col.icon;
        const items = tasks.filter((t) => t.status === col.id);
        return (
          <div
            key={col.id}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => drop(col.id)}
            data-testid={`column-${col.id}`}
            className={`rounded-2xl border bg-slate-900/30 p-3 min-h-[280px] transition-colors duration-200 ${
              dragged ? 'border-dashed border-cyan-500/50' : col.ring
            }`}
          >
            <div className="flex items-center gap-2 pb-2.5 mb-3 border-b border-slate-800">
              <span className={`w-2 h-2 rounded-full ${col.dot}`} />
              <h3 className={`text-xs font-semibold uppercase tracking-wider ${col.text}`}>{col.label}</h3>
              <span className="ml-auto text-[11px] text-slate-300">{items.length}</span>
            </div>
            <div className="space-y-2.5">
              {items.map((t) => (
                <TaskCard key={t.id} task={t} onOpen={onOpenTask} onDragStart={setDragged} blocked={isBlocked(t)} />
              ))}
              {items.length === 0 && (
                <div className="text-center py-6 text-slate-300">
                  <Icon className="w-6 h-6 mx-auto mb-1.5 opacity-40" />
                  <p className="text-[11px]">Nothing here</p>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
